import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import dayjs from 'dayjs';

import NewRegisterBtn from '../../../components/Buttons/NewRegisterBtn';
import { backendErrorNotification, frontendNotification } from '../../../components/Notification';
import { ActionButtonsContainer } from '../../../components/TablePaginator/styles';
import HttpStatus from '../../../enums/httpStatus';
import {
  tenantCustomLibsVersionsApi,
  tenantCustomVersionDownloadApi,
} from '../../../services/requests';
import ContentContainer from '../../../templates/Content/ContentContainer';

import CreateOrUpdateLibVersionModal from './Components/ModalCreate/TenantCustomLibsModalCreate';

import AsyncTable, { Column } from '../../../components/AsyncTable';
import { DownloadIcon, UpdateIcon } from '../../../icons';
import EditIcon from '../../../icons/edit';
import { TenantCustomLibVersion } from '../../../types/apiResponse/TenantCustomLibs';
import getGenericRequestPageForAsyncTable from '../../../utils/TablesFunctions';
import { downloadBlobFile } from '../../../utils/fileDownload';
import SimpleSelect from '../../../components/SimpleSelect';
import Btn from '../../../components/Buttons/Btn';

const SmartsellCustomizationJSX = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const sanitizeFilenamePart = (value: string) =>
    value
      .trim()
      .replace(/\.[a-z0-9]+$/i, '')
      .replace(/[^a-zA-Z0-9._-]+/g, '_')
      .replace(/^_+|_+$/g, '');

  const getExtensionFromPath = (path?: string) => {
    const fileName = path?.split('/').pop();
    const match = fileName?.match(/\.[a-z0-9]+$/i);
    return match?.[0] ?? '.php';
  };

  const buildDownloadFileName = (libVersion: TenantCustomLibVersion, isAuxiliaryFile: boolean) => {
    const baseName = sanitizeFilenamePart(libVersion.lib?.name ?? 'file') || 'file';
    const versionSuffix = libVersion.version ? `-v${libVersion.version}` : '';
    const auxSuffix = isAuxiliaryFile ? '-aux' : '';
    const ext = isAuxiliaryFile
      ? getExtensionFromPath(libVersion.auxiliary_file_path)
      : getExtensionFromPath(libVersion.file_path);

    return `${baseName}${versionSuffix}${auxSuffix}${ext}`;
  };

  const [isLoading, setIsLoading] = useState(false);
  const [libVersions, setLibVersions] = useState<TenantCustomLibVersion[] | []>([]);

  const [selectedItem, setSelectedItem] = useState<TenantCustomLibVersion | null>(null);
  const [showCreateOrUpdateModal, setShowCreateOrUpdateModal] = useState(false);
  const [isNewVersion, setIsNewVersion] = useState(false);
  const [updateTable, setUpdateTable] = useState(0);

  const [libVersionFilter, setLibVersionFilter] = useState('true');
  const [environmentFilter, setEnvironmentFilter] = useState('');
  const [appliedLibVersionFilter, setAppliedLibVersionFilter] = useState('true');
  const [appliedEnvironmentFilter, setAppliedEnvironmentFilter] = useState('');

  const filterOptions = [
    { value: 'true', label: t('onlyTheLatestVersion') },
    { value: 'false', label: t('allVersions') },
  ];

  const environmentOptions = [
    { value: '', label: t('allEnvironments') },
    { value: 'SMARTSELL_WEB', label: t('SMARTSELL_WEB') },
    { value: 'SMARTSELL_MOBILE', label: t('SMARTSELL_MOBILE') },
    { value: 'SMARTSELL_SALES', label: t('SMARTSELL_SALES') },
    { value: 'FASTMANAGER', label: t('FASTMANAGER') },
  ];

  const handleFilter = () => {
    setAppliedLibVersionFilter(libVersionFilter);
    setAppliedEnvironmentFilter(environmentFilter);
  };

  const query = useMemo(() => {
    let q = '?withAudiences=true';
    if (appliedLibVersionFilter === 'true') {
      q += '&onlyTheLatestVersion=true';
    }
    if (appliedEnvironmentFilter) {
      q += `&environment=${appliedEnvironmentFilter}`;
    }
    return q;
  }, [appliedLibVersionFilter, appliedEnvironmentFilter]);

  const handleLibColumn = (rowData: TenantCustomLibVersion) => {
    const { lib } = rowData;
    return <p>{lib.name}</p>;
  };

  const handleLibEnv = (rowData: TenantCustomLibVersion) => {
    const { lib } = rowData;
    return <p>{lib.smartsell_env}</p>;
  };

  const handleEditBtn = (row: TenantCustomLibVersion) => {
    setSelectedItem(row);
    setIsNewVersion(false);
    setShowCreateOrUpdateModal(true);
  };

  const handleNewVersionBtn = (row: TenantCustomLibVersion) => {
    setSelectedItem(row);
    setIsNewVersion(true);
    setShowCreateOrUpdateModal(true);
  };

  const handleDownload = async (libVersion: TenantCustomLibVersion, isAuxiliaryFile = false) => {
    try {
      setIsLoading(true);

      tenantCustomVersionDownloadApi.query = `/${libVersion.id}`;
      isAuxiliaryFile && (tenantCustomVersionDownloadApi.query += '?isAuxiliaryFile=true');
      const res = await tenantCustomVersionDownloadApi.get({});

      if (res.status === HttpStatus.OK) {
        const file = new Blob([res.data], { type: 'application/octet-stream' });

        downloadBlobFile(file, buildDownloadFileName(libVersion, isAuxiliaryFile));
      } else {
        throw res;
      }
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error(err);

      if (err?.response) {
        await backendErrorNotification(err);
        return;
      }

      frontendNotification({ message: t('error'), type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const actionsButtons = (libVersion: TenantCustomLibVersion) => (
    <ActionButtonsContainer type={3}>
      <EditIcon size="1.5rem" title={t('edit')} onClick={() => handleEditBtn(libVersion)} />
      <UpdateIcon
        size="1.5rem"
        title={t('newVersion')}
        onClick={() => handleNewVersionBtn(libVersion)}
      />
      <DownloadIcon
        size="1.5rem"
        title={`${t('download')}: ${t('mainFile')}`}
        onClick={() => handleDownload(libVersion)}
      />
      {libVersion.auxiliary_file_path && (
        <DownloadIcon
          size="1.5rem"
          title={`${t('download')}: ${t('auxiliaryFile')}`}
          onClick={() => handleDownload(libVersion, true)}
        />
      )}
    </ActionButtonsContainer>
  );

  const columns: Column<TenantCustomLibVersion>[] = [
    {
      label: t('description') as string,
      accessor: 'description',
    },
    {
      label: t('lib') as string,
      Cell: handleLibColumn,
    },
    {
      label: t('env') as string,
      Cell: handleLibEnv,
    },
    {
      label: t('moduleId') as string,
      accessor: 'module_id',
    },
    {
      label: t('version') as string,
      accessor: 'version',
    },
    {
      label: t('updatedAt') as string,
      accessor: 'updated_at',
      Cell: (row) => (row.updated_at ? dayjs(row.updated_at).format('DD-MM-YYYY HH:mm') : '-'),
    },
    {
      Cell: actionsButtons,
    },
  ];

  const handleOnCloseModal = () => {
    setShowCreateOrUpdateModal(false);
    setSelectedItem(null);
    setIsNewVersion(false);
  };

  const handleModalCallback = () => {
    setShowCreateOrUpdateModal(false);
    setSelectedItem(null);
    setIsNewVersion(false);
    setUpdateTable((u) => u + 1);
  };

  return (
    <ContentContainer title={t('smartsellCustomization')}>
      <NewRegisterBtn
        onClick={() => {
          setIsNewVersion(false);
          setShowCreateOrUpdateModal(true);
        }}
        disabled={isLoading}
        style={{ marginLeft: '0', marginBottom: '1rem' }}
      />
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ width: '250px' }}>
          <SimpleSelect
            options={filterOptions}
            value={libVersionFilter}
            onChange={(e: any) => setLibVersionFilter(e.target.value)}
            placeholder={t('select')}
            label={t('filterType') as string}
          />
        </div>
        <div style={{ width: '250px' }}>
          <SimpleSelect
            options={environmentOptions}
            value={environmentFilter}
            onChange={(e: any) => setEnvironmentFilter(e.target.value)}
            placeholder={t('select')}
            label={t('env') as string}
          />
        </div>
        <Btn
          text={t('filter') as string}
          onClick={handleFilter}
          style={{ height: '56px', margin: 0 }}
        />
      </div>

      {showCreateOrUpdateModal && (
        <CreateOrUpdateLibVersionModal
          closeModal={handleOnCloseModal}
          reqCallback={handleModalCallback}
          customLibToUpdate={selectedItem as TenantCustomLibVersion}
          isNewVersion={isNewVersion}
        />
      )}

      <AsyncTable
        tableName={t('smartsellCustomization')}
        columns={columns}
        value={libVersions}
        onChange={setLibVersions}
        requestPage={getGenericRequestPageForAsyncTable(
          tenantCustomLibsVersionsApi,
          query,
        )}
        reqListener={[updateTable, query]}
        options={{
          styles: {
            primaryColor: `${theme.colors.surface}`,
            secondaryColor: `${theme.colors.onSurface}`,
            alternateRowColor: theme.colors.onSurfaceLight,
            textColor: theme.colors.text,
          },
          quantityPerPageLabel: t('quantityPerPage'),
          searchPlaceholder: t('search'),
        }}
      />
    </ContentContainer>
  );
};

export default SmartsellCustomizationJSX;
